import type { GeoPoint } from '../dto/geo.dto.js'

/**
 * Geometry shared by the API and the web app.
 *
 * These live in `lib` rather than the web app because the numbers they produce
 * are shown to a user AND written to the database: the boundary editor displays
 * an area while someone drags a vertex, and that same figure can be saved to
 * `Site.acreage`. Two implementations would eventually disagree, and the
 * disagreement would be a site whose stated acreage does not match its drawn
 * outline.
 */

/** Mean earth radius (IUGG), in meters. */
const EARTH_RADIUS_M = 6_371_008.8

const SQUARE_METERS_PER_ACRE = 4046.8564224

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

/**
 * The area enclosed by a ring of points, in square meters.
 *
 * Uses the spherical excess formula rather than treating latitude/longitude as
 * a flat plane. A planar approximation is not merely imprecise at these
 * latitudes — it is wrong by a factor of about 1/cos(latitude), which at 33.5°
 * overstates a Lubbock park by roughly 20% (see `geo.spec.ts`, which pins that
 * number down). That is the difference between a correct quote and one that
 * prices a fifth of a park nobody is maintaining.
 *
 * The ring is treated as implicitly closed, and winding direction is ignored:
 * the absolute value is returned, so a boundary traced clockwise and one traced
 * counterclockwise describe the same park.
 */
export function polygonAreaSquareMeters(vertices: GeoPoint[]): number {
  if (vertices.length < 3) return 0

  let total = 0
  for (let index = 0; index < vertices.length; index += 1) {
    const current = vertices[index]
    const next = vertices[(index + 1) % vertices.length]
    if (current === undefined || next === undefined) continue

    total +=
      (toRadians(next.longitude) - toRadians(current.longitude)) *
      (2 +
        Math.sin(toRadians(current.latitude)) +
        Math.sin(toRadians(next.latitude)))
  }

  return Math.abs((total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2)
}

export function squareMetersToAcres(squareMeters: number): number {
  return squareMeters / SQUARE_METERS_PER_ACRE
}

/** The area enclosed by a ring, in acres — the unit `Site.acreage` holds. */
export function polygonAreaAcres(vertices: GeoPoint[]): number {
  return squareMetersToAcres(polygonAreaSquareMeters(vertices))
}

/**
 * Great-circle distance between two points, in meters.
 *
 * The haversine formula, which stays accurate for the short distances a site
 * perimeter is made of, where the more obvious law-of-cosines loses precision.
 */
export function distanceMeters(from: GeoPoint, to: GeoPoint): number {
  const lat1 = toRadians(from.latitude)
  const lat2 = toRadians(to.latitude)
  const deltaLat = lat2 - lat1
  const deltaLng = toRadians(to.longitude - from.longitude)

  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2

  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)))
}

/** The length of a closed ring's perimeter, in meters. */
export function polygonPerimeterMeters(vertices: GeoPoint[]): number {
  if (vertices.length < 2) return 0

  let total = 0
  for (let index = 0; index < vertices.length; index += 1) {
    const current = vertices[index]
    const next = vertices[(index + 1) % vertices.length]
    if (current === undefined || next === undefined) continue
    total += distanceMeters(current, next)
  }
  return total
}

/**
 * Whether a point falls inside a closed ring.
 *
 * Ray casting: count how many edges a ray from the point crosses, and an odd
 * count means inside. Like the self-intersection test, this works in the plane
 * rather than on the sphere, which is legitimate because containment is a
 * TOPOLOGICAL property — over a site-sized area the lat/lng-to-plane mapping is
 * an affine scaling, and affine transforms cannot move a point across an edge.
 * (Area is a different matter, which is why `polygonAreaSquareMeters` does the
 * spherical work.)
 *
 * A point lying exactly ON an edge is deliberately left undefined: it may come
 * back either way. Every caller here is producing an advisory warning, where a
 * coin flip about a tree planted precisely on the property line is both
 * unavoidable and harmless — and pretending to a certainty the input does not
 * carry would be worse.
 *
 * Rings spanning the antimeridian are not handled. A site is one contiguous
 * place, and none of them straddle 180°.
 */
export function isPointInRing(point: GeoPoint, vertices: GeoPoint[]): boolean {
  if (vertices.length < 3) return false

  let inside = false
  for (
    let index = 0, previous = vertices.length - 1;
    index < vertices.length;
    previous = index, index += 1
  ) {
    const a = vertices[index]
    const b = vertices[previous]
    if (a === undefined || b === undefined) continue

    // Does the edge straddle the point's latitude, and if so, does it cross to
    // the east of the point? Only eastward crossings are counted, so the ray is
    // consistently one-directional.
    const straddles = a.latitude > point.latitude !== b.latitude > point.latitude
    if (!straddles) continue

    const crossingLongitude =
      ((b.longitude - a.longitude) * (point.latitude - a.latitude)) /
        (b.latitude - a.latitude) +
      a.longitude

    if (point.longitude < crossingLongitude) inside = !inside
  }

  return inside
}

const FEET_PER_METER = 3.280839895

/**
 * Meters to feet.
 *
 * The geometry above works in meters because that is what the math wants; the
 * people reading a perimeter here work in feet, and `Site.acreage` already
 * commits the app to US customary units for size. Converting at the edge keeps
 * one unit in the calculations and the expected one on the screen.
 */
export function metersToFeet(meters: number): number {
  return meters * FEET_PER_METER
}
